import { useCallback, useEffect, useState } from 'react';
import { GetTutorialProgressAPI, TrackTutorialProgressAPI } from '../../../api/API';

const nowText = () => new Date().toISOString().slice(0, 19).replace('T', ' ');

/** Versão atual da lição ("version" em tutorials.json; sobe quando o conteúdo muda de verdade). */
export const lessonVersion = (lesson) => lesson?.version || 1;

// Eventos que a pessoa dispara com um clique mudam a tela na hora, sem esperar a API
const optimisticPatch = (event, data, version) => {
  if (event === 'watched') return { completed_at: nowText(), completed_version: version };
  if (event === 'unwatched') return { completed_at: null, completed_version: null, last_position_sec: 0 };
  if (event === 'helpful') return { helpful: data.helpful ? 1 : 0, helpful_version: version };
  return null;
};

/**
 * A lição conta como assistida (90% do vídeo, fim do vídeo ou marcada pela pessoa).
 * Uma versão nova não desfaz isso: a lição só ganha o selo "Atualizada" (isLessonUpdated).
 */
export const isLessonCompleted = (progress) => Boolean(progress?.completed_at);

/** Assistida numa versão anterior à atual: mostra o selo "Atualizada". */
export const isLessonUpdated = (lesson, progress) => (
  isLessonCompleted(progress) && (progress.completed_version || 1) < lessonVersion(lesson)
);

/** Resposta de "Esta aula ajudou?" para a versão atual (numa versão nova a pergunta volta). */
export const currentHelpful = (lesson, progress) => (
  progress && progress.helpful !== null && (progress.helpful_version || 1) === lessonVersion(lesson)
    ? progress.helpful
    : null
);

/** Quanto do vídeo a pessoa já viu, de 0 a 1. */
export const lessonWatchedRatio = (progress) => {
  if (!progress || !progress.duration_sec) return 0;
  return Math.min(progress.max_position_sec / progress.duration_sec, 1);
};

/** Quantas das lições estão assistidas. */
export const countCompleted = (lessons, progressById) => (
  (lessons || []).filter((lesson) => isLessonCompleted(progressById[lesson.id])).length
);

/**
 * Progresso do usuário logado nos tutoriais, indexado pelo id da lição, um nome fixo pelo
 * assunto (ex.: criar-agendamento) que nunca muda: reordenar, inserir lições ou trocar o arquivo
 * do vídeo no tutorials.json não mexe no progresso. Cada evento grava também a versão da lição.
 * @param {Boolean} enabled - carrega só quando a tela precisa (o atalho carrega ao abrir)
 * @returns {{ progressById: Object, loaded: Boolean, track: Function }}
 */
const useTutorialProgress = (enabled = true) => {
  const [progressById, setProgressById] = useState({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    GetTutorialProgressAPI((response) => {
      if (!active) return;
      const rows = response && response.success !== false && Array.isArray(response.progress) ? response.progress : [];
      setProgressById(Object.fromEntries(rows.map((row) => [row.lesson_id, row])));
      setLoaded(true);
    });
    return () => { active = false; };
  }, [enabled]);

  /** Grava um evento da lição: open | progress | complete | watched | unwatched | helpful. */
  const track = useCallback((lesson, event, data = {}) => {
    const lessonId = lesson?.id;
    if (!lessonId) return;
    const version = lessonVersion(lesson);
    const patch = optimisticPatch(event, data, version);
    if (patch) {
      setProgressById((prev) => ({ ...prev, [lessonId]: { lesson_id: lessonId, ...prev[lessonId], ...patch } }));
    }
    TrackTutorialProgressAPI({ lesson_id: lessonId, version, event, ...data }, (response) => {
      if (response?.success && response.progress) {
        setProgressById((prev) => ({ ...prev, [lessonId]: response.progress }));
      }
    });
  }, []);

  return { progressById, loaded, track };
};

export default useTutorialProgress;
